'use client';

import React from 'react';
import { Check } from 'lucide-react';
import { LiveMessage } from '../../data/liveMessageData/liveMessage';
import { AdvancedImage } from '@cloudinary/react';
import PopupTemplate from '@/app/ui/popupTemplate';
import { useTranslations } from 'next-intl';

interface ImageSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  images: LiveMessage[];
  selectedImageUri?: string | null;
  onImageSelect: (message: LiveMessage) => void;
  onApply: () => void;
}

export default function ImageSelectionPopup({ 
  isOpen, 
  onClose, 
  images,
  selectedImageUri,
  onImageSelect,
  onApply
}: ImageSelectionPopupProps) {
  const t = useTranslations('chat');

  const handleImageClick = (message: LiveMessage) => {
    onImageSelect(message);
  };

  const handleApply = () => {
    onApply();
    onClose();
  };

  return (
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
      className="max-h-[90vh] flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <h2 className="text-white font-['Montserrat',Helvetica] text-lg font-medium">
            {t('selectImageAsReference')}
          </h2>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto dark-scrollbar p-4">
        <div className="grid gap-3 justify-items-start" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {images.map((message) => {
            const isMsgAssistantImage = message.role === 'assistant' && message.imageUrl;
            const isSelected = selectedImageUri === message.imageUrl;
            
            if (!isMsgAssistantImage) return null;

            return (
              <div 
                key={message.id} 
                className={`relative group cursor-pointer rounded-lg overflow-hidden transition-all duration-200 ${
                  isSelected 
                    ? 'ring-2 ring-[#5856d6] shadow-lg shadow-[#5856d6]/25' 
                    : 'hover:ring-2 hover:ring-white/20'
                }`}
                onClick={() => handleImageClick(message)}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 z-10 bg-[#5856d6] rounded-full p-1">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* Image status text for assistant messages with images - only show during loading */}
                {message.imageWithStatus.loadingStatus === 'loading' && (
                  <div className="aspect-[9/16] flex items-center justify-center bg-[#2a2a2e] rounded-lg">
                    <div className="text-white/40 text-xs">{t('loading')}</div>
                  </div>
                )}
                
                {/* Show Cloudinary image when loaded */}
                {message.imageWithStatus.loadingStatus === 'loaded' && message.imageWithStatus.imageData && (
                  <div className="aspect-[9/16] rounded-lg overflow-hidden">
                    <AdvancedImage
                      cldImg={message.imageWithStatus.imageData}
                      alt={message.content || t('generatedImage')}
                      className="w-full h-full object-cover"
                      style={{ 
                        objectFit: 'cover',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}
                      loading="lazy"
                    />
                  </div>
                )}
                
                {/* Show "Image failed to load" message when image status is failed */}
                {message.imageWithStatus.loadingStatus === 'failed' && (
                  <div className="aspect-[9/16] flex items-center justify-center bg-[#2a2a2e] rounded-lg">
                    <div className="text-white/40 text-xs">×</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer with Apply button */}
      <div className="flex-shrink-0 p-4 border-t border-white/10">
        <div className="flex justify-end">
          <button
            onClick={handleApply}
            className="px-6 py-2 bg-[#5856d6] hover:bg-[#5856d6] text-white rounded-xl transition-colors duration-200 font-medium"
          >
            {t('apply')}
          </button>
        </div>
      </div>
    </PopupTemplate>
  );
}
